import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { TemplateState } from '../../../types/common/types';
import {
  WEB_COMPONENT_GZ_WIZARD,
  WEB_COMPONENT_INTEGRATION_EDIT_INTEGRATION,
  WEB_COMPONENT_INTEGRATION_REQUIREMENTS,
  WEB_COMPONENT_INTEGRATION_SAVED_SUCCESS,
  WEB_COMPONENT_SELECT_COMPANY_INTEGRATION,
  WEB_COMPONENT_SELECT_INTEGRATION,
} from '../../../webcomponents.constants';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { CreateIntegrationEvent } from '../types/CreateIntegrationEvent';
import { SelectIntegrationTypeWebComponent } from '../../selectIntegrationType';
import { EditIntegrationWebComponent } from '../../editIntegration';
import { IntegrationRequirementsWebComponent } from '../../integrationRequirements';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { IntegrationSavedSuccessWebComponent } from '../../integrationSavedSuccess';
import { ButtonColors } from '../../../ui/gzButton/types';
import { IntegrationSelectCompanyWebComponent } from '../../integrationSelectCompany';
import { GzWizardWebComponent } from '../../../ui/gzWizard';
import { PartnerAllowedIntegrationSteps } from '../types/PartnerAllowedIntegrationSteps';
import { CustomerAllowedIntegrationSteps } from '../types/CustomerAllowedIntegrationSteps';
import { GzTooltipWebComponent } from '../../../ui/gzTooltip';

export class IntegrationCreatePanelView extends BaseWebComponentView {
  public formGroup!: FormGroup;
  public allowedSteps;

  public dom: {
    $wrapper: HTMLElement;
    $integrationSelectCompany: IntegrationSelectCompanyWebComponent;
    $integrationTypeList: SelectIntegrationTypeWebComponent;
    $cancelButton: GzButtonWebComponent;
    $applyButtonTooltip: GzTooltipWebComponent;
    $applyButton: GzButtonWebComponent;
    $nextButton: GzButtonWebComponent;
    $backButton: GzButtonWebComponent;
    $closeButton: GzButtonWebComponent;
    $addNewButton: GzButtonWebComponent;
    $successWindow: IntegrationSavedSuccessWebComponent;
    $confirmButton: GzButtonWebComponent;
    $selectIntegrationButtons: GzButtonWebComponent[];
    $editIntegration: EditIntegrationWebComponent;
    $requirements: IntegrationRequirementsWebComponent;
    $wizard: GzWizardWebComponent;
  };

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),

      $integrationSelectCompany: this.shadow.querySelector(WEB_COMPONENT_SELECT_COMPANY_INTEGRATION),
      $integrationTypeList: this.shadow.querySelector(WEB_COMPONENT_SELECT_INTEGRATION),
      $cancelButton: this.shadow.querySelector('#cancel-button'),
      $applyButtonTooltip: this.shadow.querySelector('#apply-button-tooltip'),
      $applyButton: this.shadow.querySelector('#apply-button'),
      $nextButton: this.shadow.querySelector('#next-button'),
      $backButton: this.shadow.querySelector('#back-button'),
      $confirmButton: this.shadow.querySelector('#confirm-button'),
      $closeButton: this.shadow.querySelector('#close-button'),
      $addNewButton: this.shadow.querySelector('#add-new-button'),
      $successWindow: this.shadow.querySelector(WEB_COMPONENT_INTEGRATION_SAVED_SUCCESS),
      $selectIntegrationButtons: [],
      $editIntegration: this.shadow.querySelector(WEB_COMPONENT_INTEGRATION_EDIT_INTEGRATION),
      $requirements: this.shadow.querySelector(WEB_COMPONENT_INTEGRATION_REQUIREMENTS),
      $wizard: this.shadow.querySelector(WEB_COMPONENT_GZ_WIZARD),
    };

    this.setTranslatedStrings();
  }

  setTranslatedStrings() {
    this.dom.$cancelButton.innerText = this.dependencies.translateFn('Cancel');
    this.dom.$confirmButton.innerText = this.dependencies.translateFn('Confirm');
    this.dom.$addNewButton.innerText = this.dependencies.translateFn('Add new');
    this.dom.$closeButton.innerText = this.dependencies.translateFn('Done');
    this.dom.$backButton.innerText = this.dependencies.translateFn('Back');
    this.dom.$nextButton.innerText = this.dependencies.translateFn('Next');
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  buildSuccessView(data) {
    this.setAllowedSteps();
    this.setWizardOptions();
    this.dom.$integrationSelectCompany.injectDependencies(this.dependencies);
    this.dom.$integrationTypeList.injectDependencies(this.dependencies);
    this.dom.$integrationTypeList.licenses = data.licenses;
    this.dom.$editIntegration.injectDependencies(this.dependencies);
    this.dom.$requirements.injectDependencies(this.dependencies);
    this.dom.$successWindow.injectDependencies(this.dependencies);
    const startWebCompPromises: Promise<void>[] = [
      this.dom.$integrationSelectCompany.start(),
      this.dom.$integrationTypeList.start(),
      this.dom.$editIntegration.start(),
      this.dom.$requirements.start(),
      this.dom.$successWindow.start(),
    ];

    Promise.all(startWebCompPromises)
      .then(() => {
        this.removeLoadingState();
        this.dom.$wrapper.dispatchEvent(
          new CustomEvent(CreateIntegrationEvent.OnViewInit, {
            bubbles: true,
            composed: true,
          })
        );
      })
      .catch(console.error);
    this.dom.$cancelButton.onmouseover = function (event) {
      const button = event.target as GzButtonWebComponent;
      button.color = ButtonColors.PRIMARY;
    };
    this.dom.$cancelButton.onmouseleave = function (event) {
      const button = event.target as GzButtonWebComponent;
      button.color = ButtonColors.SECONDARY;
    };
  }

  buildErrorView(error: Error) {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private setWizardOptions() {
    this.dom.$wizard.options = [];
    if (this.dependencies.isPartnerCompany()) {
      this.dom.$wizard.options.push({
        step: this.allowedSteps.SelectIntegrationCompany,
        title: this.dependencies.translateFn('Select company'),
        message: '',
      });
    }
    this.dom.$wizard.options.push(
      {
        step: this.allowedSteps.SelectIntegrationType,
        title: this.dependencies.translateFn('Select sensor'),
        message: '',
      },
      {
        step: this.allowedSteps.ShowRequirements,
        title: this.dependencies.translateFn('Check requirements'),
        message: '',
      },
      {
        step: this.allowedSteps.AddIntegration,
        title: this.dependencies.translateFn('Sensor details'),
        message: '',
      },
      {
        step: this.allowedSteps.SuccessView,
        title: this.dependencies.translateFn('Finalize'),
        message: '',
      }
    );
  }

  private setAllowedSteps() {
    this.allowedSteps = this.dependencies.isPartnerCompany()
      ? PartnerAllowedIntegrationSteps
      : CustomerAllowedIntegrationSteps;
  }

  public translateSensorConnectivityErrorCodes(errorCode: number): string {
    return this.stringsTranslator.translateSensorConnectivityErrorCodes(errorCode);
  }
}
